import { format } from "date-fns";
import { ScheduleTrans, ProfileTrans } from "../../utils/translations";
import { isOnlineClubPass } from "../../utils/passHelpers";
import { getPassCardTheme } from "../../utils/passCardThemes";

/** ScheduleTrans onlineClub label for any onlineClub-type pass */
function passDisplayName(pass, details, translatePassName) {
  if (isOnlineClubPass(pass) && details.onlineClub) return details.onlineClub;
  return translatePassName(pass.passTypeId || pass.name);
}

export default function ActivePassDisplay({
  user,
  language,
  isPassExhausted,
  translatePassName,
  translateMonth,
  dataAttribute,
  onPayUnpaid,
}) {
  const details = ScheduleTrans[language];
  const profileDetails = ProfileTrans[language];
  const now = new Date();

  // Paid passes: active, exhausted, or paused, not expired
  const activePasses = user?.passes?.filter(
    (pass) =>
      pass.paid !== false &&
      (pass.status === "active" || pass.status === "exhausted" || pass.status === "paused") &&
      new Date(pass.expiresAt) > now
  );

  // Unpaid passes: show in yellow with Pay button
  const unpaidPasses = user?.passes?.filter(
    (pass) =>
      pass.paid === false &&
      (pass.status === "active" || pass.status === "exhausted" || pass.status === "paused") &&
      new Date(pass.expiresAt) > now
  );

  if (!activePasses?.length && !unpaidPasses?.length) return null;

  return (
    <div data-active-pass={dataAttribute} className="w-11/12 mx-auto mb-6 mt-0 flex flex-col items-center">
      {/* Unpaid passes - same design and size as Profile; desktop: 2 per row, centered */}
      {unpaidPasses?.length > 0 && (
        <div className="mb-4 w-full flex flex-wrap justify-center gap-4 md:max-w-[576px] md:mx-auto">
          {unpaidPasses.map((pass) => (
            <div
              key={pass._id}
              className={`border-2 rounded-xl px-6 shadow-lg p-4 w-full max-w-[280px] min-w-[200px] md:!w-[280px] md:flex-shrink-0 ${
                pass.status === "paused" ? "bg-blue-50 border-blue-400" : "bg-amber-50 border-amber-400"
              }`}
            >
              <div className="flex flex-col items-center text-center">
                <h3 className={`text-base font-semibold mb-2 ${pass.status === "paused" ? "text-blue-800" : "text-amber-800"}`}>
                  {translatePassName(pass.passTypeId || pass.name)}
                </h3>
                <p className={`text-sm mb-1 ${pass.status === "paused" ? "text-blue-700" : "text-amber-700"}`}>
                  {profileDetails.unpaidPass} — ₪{pass.finalPrice}
                </p>
                <div className={`text-sm w-full ${pass.status === "paused" ? "text-blue-700" : "text-amber-700"}`}>
                  {pass.classesIncluded != null ? (
                    <>
                      <p>
                        {pass.classesUsed ?? 0} / {pass.classesIncluded} {details.classesUsed}
                      </p>
                      <div className={`w-full rounded-full h-2 mt-2 overflow-hidden ${pass.status === "paused" ? "bg-blue-200" : "bg-amber-200"}`}>
                        <div
                          className={`h-full transition-all ${pass.status === "paused" ? "bg-blue-500" : "bg-amber-500"}`}
                          style={{
                            width: `${Math.min(100, ((pass.classesUsed ?? 0) / pass.classesIncluded) * 100)}%`,
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="font-semibold">♾️ {details.unlimited}</p>
                  )}
                  <p className={`mt-2 text-xs ${pass.status === "paused" ? "text-blue-600" : "text-amber-600"}`}>
                    {pass.status === "paused"
                      ? details.paused
                      : `${details.expiresOn}: ${format(new Date(pass.expiresAt), "dd")} ${translateMonth(format(new Date(pass.expiresAt), "MMM"))} ${format(new Date(pass.expiresAt), "yyyy")}`}
                  </p>
                </div>
                {typeof onPayUnpaid === "function" && (
                  <button
                    type="button"
                    onClick={() => onPayUnpaid(pass._id)}
                    className="mt-3 px-4 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 text-sm"
                  >
                    {details.payForPass}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Paid active passes - same size as Profile; desktop: 2 per row, centered */}
      {activePasses?.length > 0 && (
        <div className="w-full flex flex-wrap justify-center gap-4 md:max-w-[576px] md:mx-auto">
          {activePasses.map((pass) => {
            const isPaused = pass.status === "paused";
            const exhausted = isPassExhausted(pass);
            // Pass-type color only applies to the normal active look; paused/exhausted are
            // status colors and always win, regardless of the pass type's chosen color.
            const theme = getPassCardTheme(pass.passTypeId?.color);
            const cardBg = isPaused
              ? "bg-blue-50 border-blue-500"
              : exhausted
                ? "bg-orange-50 border-orange-400"
                : `${theme.bg} ${theme.border}`;
            const titleCls = isPaused
              ? "text-blue-800"
              : exhausted
                ? "text-gray-700"
                : theme.title;
            const bodyCls = isPaused
              ? "text-blue-700"
              : exhausted
                ? "text-gray-600"
                : theme.body;
            const trackCls = isPaused
              ? "bg-blue-200"
              : exhausted
                ? "bg-orange-200"
                : theme.track;
            const barCls = isPaused
              ? "bg-blue-500"
              : exhausted
                ? "bg-orange-400"
                : theme.bar;
            return (
              <div
                key={pass._id}
                className={`border-2 rounded-xl px-6 shadow-lg p-4 w-full max-w-[280px] min-w-[200px] md:!w-[280px] md:flex-shrink-0 ${cardBg}`}
              >
                <div className="flex flex-col items-center text-center">
                  <h3 className={`text-base font-semibold mb-2 ${titleCls}`}>
                    {passDisplayName(pass, details, translatePassName)}
                  </h3>
                  <div className={`text-sm w-full ${bodyCls}`}>
                    {pass.classesIncluded ? (
                      <>
                        <p>
                          {pass.classesUsed} / {pass.classesIncluded} {details.classesUsed}
                        </p>
                        <div className={`w-full rounded-full h-2 mt-2 overflow-hidden ${trackCls}`}>
                          <div
                            className={`h-full ${barCls}`}
                            style={{
                              width: `${(pass.classesUsed / pass.classesIncluded) * 100}%`,
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <p className="font-semibold">♾️ {details.unlimited}</p>
                    )}

                    <p className="mt-2 text-xs">
                      {isPaused
                        ? details.paused
                        : `${pass.isSubscription && pass.subscriptionActive ? details.nextBillingDate : details.expiresOn}: ${format(new Date(pass.expiresAt), "dd")} ${translateMonth(format(new Date(pass.expiresAt), "MMM"))} ${format(new Date(pass.expiresAt), "yyyy")}`}
                    </p>
                    {!isPaused && isPassExhausted(pass) && (
                      <p className="mt-2 text-xs text-green-600 font-medium">
                        {profileDetails.passExhausted}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
